import React from "react"
import { CollapsibleFormField, FormField, FormFieldDirection, FormFieldNote, FormFieldNoteType, renderReadonly } from "@ui/common/form/FormField"
import { TextInput, TextInputType } from "@ui/common/form/TextInput"
import { TextArea } from "@ui/common/form/TextArea"
import { Button, ButtonTheme, ButtonType } from "@ui/common/buttons/Button"
import { AttachmentList } from "@ui/common/form/AttachmentList"
import { ServiceItemViewModel } from "@ui/workRequests/ServiceItemViewModel"
import { useBloc, useBlocCoordinator } from "@lib/bloc/hooks"
import {
    labelForServiceCode,
    ServiceRequestType
} from "@model/serviceRequests/ServiceRequest"
import { Select, SelectTheme } from "@ui/common/form/Select"
import { safeParseInt } from "@lib/TypeUtil"
import {
    serviceItemEmptyFieldLabel,
    serviceItemHasCostValue,
    serviceItemLaborTimeLabel,
} from "@model/serviceRequests/ServiceItemForm"
import { MultiSelect } from "@ui/common/form/MultiSelect"
import { Contact, labelForContact } from "@model/contacts/Contact"
import { Grid, GridCont, GridGap } from "@ui/common/grids/Grid"
import { safeParseFloat } from "../../lib/pdf/util/TypeUtil"
import { WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"
import { CompletableTaskValue, InspectionTaskValue } from "@model/Task"

import { LoadingIndicator } from "@ui/common/LoadingIndicator"
import TaskList from "@ui/common/TaskList"
import { is } from "date-fns/locale"
import { RemoveButton } from "@ui/common/buttons/RemoveButton"
import { ImageUrls } from "@ui/common/images/ImageUrls"
import { HistoryTable } from "@ui/common/tables/HistoryTable"
import { DI } from "@ui/DI"

export function ServiceItem({
    viewModel,
    onRemoveClicked,
    onCompleteClicked,
    onDeclineClicked,
    onFollowUpClicked,
    onViewFollowUpClicked,
    index,
    isEnabled = true,
    isClosedWorkOrder = false
}: {
    viewModel: ServiceItemViewModel
    onRemoveClicked: () => void
    onCompleteClicked: () => void
    onDeclineClicked: () => void
    onFollowUpClicked?: () => void
    onViewFollowUpClicked?: () => void
    index: number
    isEnabled?: boolean
    isClosedWorkOrder?: boolean
}) {
    const [state] = useBlocCoordinator(() => viewModel)
    const [createNewWorkOrderScreenState] = useBloc(() => DI.createNewWorkOrderScreenBloc())
    const isDeclined = !!(state.dismissedReason && state.dismissedReason.trim() !== "")
    // The current step's parent schedule is on the step itself (serviceScheduleId).
    // The asset is "subscribed" to that schedule if any AssetSchedule entry points
    // to a ServiceScheduleItem (step) whose parent schedule matches.
    const currentScheduleId = state.step?.serviceScheduleId ?? state.schedule?.id ?? null
    const isAssetOnCurrentSchedule =
        currentScheduleId != null &&
        createNewWorkOrderScreenState.AssetSchedule.some((entry) => {
            const matchingStep = state.serviceScheduleStepsInUse.find(
                (s) => s.id === entry.serviceScheduleItemId
            )
            return matchingStep?.serviceScheduleId === currentScheduleId
        })
    const showStepDropdown = state.step != null && isAssetOnCurrentSchedule
    const isIncomplete = (
        state.workRequestType === WorkRequestType.Preventative ||
        state.workRequestType === WorkRequestType.Inspection
    ) && (state.tasks?.some(
        (t) => t.value === InspectionTaskValue.Fail ||
               t.value === InspectionTaskValue.NotPerformed ||
               t.value === CompletableTaskValue.NotPerformed
    ) ?? false)
    const showDeleteActionString = (state.workRequestType == WorkRequestType.Service && !state.id) ||
        ((state.id ?? 0) > 0 && (state.workOrderId ?? 0) > 0 && (createNewWorkOrderScreenState.workOrder?.id ?? 0) > 0)

    // Action Item 10452: the collapsed card shows Due Hours/Miles/Date while the Service
    // Item is still open, and swaps to Labor Time/Total Cost once it is completed.
    const isCompleted = state.status === WorkRequestStatus.Done
    const totalCost =
        (safeParseFloat(state.laborCost) ?? 0) +
        (safeParseFloat(state.partsCost) ?? 0) +
        (safeParseFloat(state.otherCost) ?? 0)
    // Action Item 10811: Actual Labor Hours / Labor Cost / Parts Cost / Misc Cost have no default
    // value, so a field the user never filled in reads as "-" instead of 0h 0m or $0.
    const laborTimeLabel = serviceItemLaborTimeLabel(state)
    const costLabel = (cost: string | null) =>
        serviceItemHasCostValue(cost) ? `$${cost}` : serviceItemEmptyFieldLabel
    const costAmountLabel = (cost: string | null) =>
        serviceItemHasCostValue(cost) ? `$${safeParseFloat(cost).toFixed(2)}` : serviceItemEmptyFieldLabel
    const workPerformedByLabel =
        state.workPerformedBy.length > 0
            ? state.mechanics
                  .filter((contact) => state.workPerformedBy.includes(contact.id))
                  .map((contact) => labelForContact(contact))
                  .join(", ")
            : "-"

    return (
        <>
            {state.isLoading ? (
                <LoadingIndicator isLoading />
            ) : (
                <>
                    <div className="service-item-card">
                        <div className={`card-header ${state.isSaved ? "readonly-header" : ""}`}>
                            {state.isSaved ? (
                                <>
                                    <div className="left">
                                        <div className="service-id">
                                            SERVICE #: {state.id ? state.id.toString().padStart(6, "0") : "PENDING"}
                                            {isIncomplete && <span className="incomplete-tag" style={{ marginLeft: "0.25rem" }}>Incomplete</span>}
                                        </div>
                                        <div className="service-name">{state.name || "Service Item"}</div>
                                    </div>

                                    <div className="center">
                                        {isCompleted ? (
                                            <div className="status">
                                                <div className="status-block">
                                                    Labor Time
                                                    <strong>{laborTimeLabel}</strong>
                                                </div>
                                                <div className="status-block">
                                                    Total Cost
                                                    <strong>${totalCost.toFixed(2)}</strong>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="due-info">
                                                <div className="due-block">
                                                    <div className="label">Due Hours</div>
                                                    <div className="value">{state.dueHourMeter ?? "-"}</div>
                                                </div>
                                                <div className="due-block">
                                                    <div className="label">Due Miles</div>
                                                    <div className="value">
                                                        {state.dueOdometer != null ? state.dueOdometer.toLocaleString() : "-"}
                                                    </div>
                                                </div>
                                                <div className="due-block">
                                                    <div className="label">Due Date</div>
                                                    <div className="value">
                                                        {state.dueDate ? new Date(state.dueDate).toLocaleDateString() : "-"}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="right">
                                        <div className="actions">
                                            {isIncomplete && state.childFollowUpId == null && !isClosedWorkOrder && onFollowUpClicked && (
                                                <Button
                                                    label="Follow-Up"
                                                    type={ButtonType.Outlined}
                                                    onClick={onFollowUpClicked}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                            {state.childFollowUpId != null && onViewFollowUpClicked && (
                                                <Button
                                                    label="View Follow-Up"
                                                    type={ButtonType.Outlined}
                                                    onClick={onViewFollowUpClicked}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                            {state.status !== WorkRequestStatus.Done && (
                                                <Button
                                                    label="Complete"
                                                    type={ButtonType.Outlined}
                                                    onClick={() => viewModel.onCompleteClicked()}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                            {(state.status === WorkRequestStatus.Done && !isClosedWorkOrder) && (
                                                <Button
                                                    label="Reopen"
                                                    type={ButtonType.Outlined}
                                                    onClick={() => viewModel.onReopenClicked()}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                            <Button
                                                label="Edit"
                                                type={ButtonType.Outlined}
                                                onClick={() => viewModel.updateField("isSaved", false)}
                                                isEnabled={isEnabled}
                                            />
                                            {viewModel.canDeleteWorkRequest() && (
                                                <Button
                                                    label={showDeleteActionString ? "Delete" : "Decline"}
                                                    type={ButtonType.Outlined}
                                                    onClick={onRemoveClicked}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                            {viewModel.canDeclineWorkRequest() && (
                                                <Button
                                                    label="Decline"
                                                    type={ButtonType.Outlined}
                                                    onClick={onDeclineClicked}
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="left">
                                        <div className="item-badge">{index + 1}</div>
                                        <div className="item-title">
                                            <div className="title">{state.serviceType === ServiceRequestType.FollowUp && state.name ? state.name : "Service Item"}</div>
                                            <div className="subtitle">  SERVICE #: {state.id ? state.id.toString().padStart(6, "0") : "PENDING"}</div>
                                        </div>
                                    </div>

                                    <div className="status">
                                        <div className="status-block">
                                            Labor Time
                                            <strong>{laborTimeLabel}</strong>
                                        </div>
                                        <div className="status-block">
                                            Total Cost
                                            <strong>${totalCost.toFixed(2)}</strong>
                                        </div>
                                    </div>

                                    <div className="actions">
                                        {state.isSaved && isIncomplete && state.childFollowUpId == null && !isClosedWorkOrder && onFollowUpClicked && (
                                            <Button
                                                label="Follow-Up"
                                                type={ButtonType.Outlined}
                                                onClick={onFollowUpClicked}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                        {state.isSaved && state.childFollowUpId != null && onViewFollowUpClicked && (
                                            <Button
                                                label="View Follow-Up"
                                                type={ButtonType.Outlined}
                                                onClick={onViewFollowUpClicked}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                        {state.isSaved && state.status !== WorkRequestStatus.Done && (
                                            <>
                                                <Button label="Complete" type={ButtonType.Outlined}
                                                    onClick={() => viewModel.onCompleteClicked()}
                                                    isEnabled={isEnabled} />
                                            </>
                                        )}
                                        {(state.isSaved && state.status === WorkRequestStatus.Done && !isClosedWorkOrder) && (
                                            <>
                                                <Button label="Reopen" type={ButtonType.Outlined}
                                                    onClick={() => viewModel.onReopenClicked()}
                                                    isEnabled={isEnabled} />
                                            </>
                                        )}
                                        <Button
                                            label={state.isSaved ? "Edit" : "Save"}
                                            type={state.isSaved ? ButtonType.Outlined : ButtonType.Contained}
                                            isEnabled={
                                                state.name.trim().length != 0 && isEnabled
                                            }
                                            onClick={() => {
                                                viewModel.updateField("isSaved", !state.isSaved)
                                                if(!state.isSaved && (state.workRequestType === WorkRequestType.Preventative ||
                                                    state.workRequestType === WorkRequestType.Inspection))
                                                    viewModel.updateField("initialStep", state.step)
                                                    viewModel.updateField("initialTasks", state.tasks)
                                                    //initialTasks and initialStep is used by the stepChanged to re set the task when the initial value is re selected.
                                                    //But when save button is clicked we have to consider the current step and tasks as initial values.
                                                }
                                        }
                                        />
                                        {viewModel.canDeleteWorkRequest() && (
                                            <Button
                                                label={showDeleteActionString ? "Delete" : "Decline"}
                                                type={ButtonType.Outlined}
                                                onClick={onRemoveClicked}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                        {viewModel.canDeclineWorkRequest() && (
                                            <Button
                                                label="Decline"
                                                type={ButtonType.Outlined}
                                                onClick={onDeclineClicked}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                        </div>
                        {state.status == WorkRequestStatus.Done && (
                            <div className="completed-banner" role="status" aria-live="polite">
                                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M20.285 6.709l-11.025 11.025-5.546-5.547 1.414-1.414 4.132 4.132 9.611-9.611z" />
                                </svg>
                                <div className="content">
                                    <div className="title">WORK COMPLETED</div>
                                    <div className="reason">
                                        {/*{`Completed on ${new Date(new Date()).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}`}*/}
                                    </div>
                                </div>
                            </div>
                        )}
                        {isDeclined && (
                            <div className="declined-banner" role="status" aria-live="polite">
                                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 2L1 21h22L12 2zm0 14a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm-1-9h2v7h-2V7z" />
                                </svg>
                                <div className="content">
                                    <div className="title">WORK DECLINED</div>
                                    <div className="reason">{state.dismissedReason}</div>
                                </div>
                            </div>
                        )}

                        {state.isSaved ? (
                            <div className="readonly-body service-item-readonly">
                                <div className="row">
                                    <div className="col">
                                            <strong>Service Code:</strong>{" "}
                                            {state.serviceCode ? (state.serviceCodes?.find(x => x.id === state.serviceCode?.id))?.code : "-"}
                                    </div>
                                    <div className="col notes">
                                        <strong>Notes:</strong>{" "}
                                        {state.notes && state.notes.trim().length > 0 ? state.notes : "-"}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <strong>Work Performed By:</strong> {workPerformedByLabel}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <strong>Actual Labor Hours:</strong> {laborTimeLabel}
                                    </div>
                                    <div className="col">
                                        <strong>Actual Labor Cost:</strong> {costAmountLabel(state.laborCost)}
                                    </div>
                                    <div className="col">
                                        <strong>Actual Parts Cost:</strong> {costAmountLabel(state.partsCost)}
                                    </div>
                                    <div className="col">
                                        <strong>Misc Cost:</strong> {costAmountLabel(state.otherCost)}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <GridCont gap={GridGap.Small}>
                                <Grid columns={1}>
                                    {(state.workRequestType == WorkRequestType.Preventative || state.workRequestType == WorkRequestType.Inspection) && state.serviceType !== ServiceRequestType.FollowUp  &&
                                        (showStepDropdown ?
                                                <FormField label="Step">
                                                    <Select
                                                        theme={SelectTheme.Dark}
                                                        options={
                                                            state.serviceScheduleStepsInUse
                                                                .filter((it) => it.serviceScheduleId === (state.schedule?.id ?? state.step?.serviceScheduleId))
                                                                .sort((a, b) => (a.step > b.step ? 1 : b.step > a.step ? -1 : 0))
                                                                .map((it) => ({
                                                                    label: `${it.label}`,
                                                                    value: it.id.toString(),
                                                                }))
                                                        }
                                                        value={state.step?.id}
                                                        onChange={(value) => viewModel.stepChanged(value)}
                                                        isEnabled={isEnabled && !isClosedWorkOrder}
                                                    />
                                                </FormField>
                                                :
                                                <FormField label="Service Item Name *">
                                                        {state.isSaved ? (
                                                renderReadonly(state.name)
                                            ) : (
                                                <TextInput
                                                    type={TextInputType.Text}
                                                    value={state.name}
                                                    onChange={(v) => {
                                                        viewModel.updateField("name", v)
                                                    }}
                                                    isEnabled={isEnabled}
                                                    outlineInvalid={isEnabled && state.name.trim().length === 0}
                                                />
                                            )}
                                                </FormField>
                                    )}
                                    {(state.workRequestType == WorkRequestType.Service || state.serviceType === ServiceRequestType.FollowUp) &&
                                        <FormField label="Service Item Name *">
                                            {state.isSaved ? (
                                                renderReadonly(state.name)
                                            ) : (
                                                <TextInput
                                                    type={TextInputType.Text}
                                                    value={state.name}
                                                    onChange={(v) => {
                                                        viewModel.updateField("name", v)
                                                    }}
                                                    isEnabled={isEnabled}
                                                    outlineInvalid={isEnabled && state.name.trim().length === 0}
                                                />
                                            )}
                                        </FormField>
                                    }
                                    {/* Action Item 6767: Work Performed By moved from the Work
                                        Order level to the Service Item level (stays multi-select). */}
                                    <FormField label="Work Performed By">
                                        <MultiSelect
                                            options={state.mechanics
                                                .filter((it) => !it.deleted)
                                                .map((contact) => ({
                                                    key: contact.id,
                                                    value: contact.id.toString(),
                                                    label: labelForContact(contact),
                                                    isChecked: state.workPerformedBy.includes(contact.id),
                                                }))}
                                            onSelectionChanged={(selection) =>
                                                viewModel.updateField(
                                                    "workPerformedBy",
                                                    selection.map((id) => safeParseInt(id))
                                                )
                                            }
                                            disabled={!isEnabled}
                                        />
                                    </FormField>
                                    {state.workRequestType == WorkRequestType.Service && !state.isSaved &&
                                        <FormField label="Notify Contacts">
                                            <MultiSelect
                                                options={state.contacts
                                                    .filter((it) => !it.deleted && it.email != null)
                                                    .map((contact) => ({
                                                        key: contact.id,
                                                        value: contact.id.toString(),
                                                        label: labelForContact(contact),
                                                        isChecked: state.notifyContacts.includes(contact.id),
                                                    }))}
                                                onSelectionChanged={(selection) =>
                                                    viewModel.updateField(
                                                        "notifyContacts",
                                                        selection.map((id) => safeParseInt(id))
                                                    )
                                                }
                                                disabled={!isEnabled}
                                            />
                                        </FormField>
                                    }
                                </Grid>

                                <Grid columns={2}>
                                    <FormField label="Service Code">
                                                {state.isSaved ? (
                                                    renderReadonly((state.serviceCodes?.find(x => x.id === state.serviceCode?.id))?.code)
                                        ) : (
                                            <Select
                                                options={[
                                                    ...[{ id: -1, value: "-1", label: "Select Service Code" }].map((s) => ({
                                                        key: s.id,
                                                        value: `${s.value}`,
                                                        label: s.label,
                                                    })),
                                                     ...state.serviceCodes.map((it) => ({
                                                        key: it.id,
                                                        value: `${it.id}`,
                                                        label: labelForServiceCode(it),
                                                    })),
                                                ]}
                                                onChange={(value) => {
                                                    viewModel.updateField(
                                                        "serviceCode",
                                                        viewModel.state.serviceCodes.find((it) => it.id === safeParseInt(value)) ?? null
                                                    )
                                                }}
                                                value={state.serviceCode?.id}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                    </FormField>
                                </Grid>

                                <Grid columns={4}>
                                    <FormField label="Actual Labor Hours">
                                        {state.isSaved ? (
                                            renderReadonly(laborTimeLabel)
                                        ) : (
                                            <div className="labor-time-fields">
                                                <TextInput
                                                    type={TextInputType.Integer}
                                                    value={state.laborHours?.toString() ?? ""}
                                                    onChange={(v) => viewModel.updateField("laborHours", v ? Number(v) : null)}
                                                    onFocus={(event) => event.target.select()}
                                                    min={0}
                                                    isEnabled={isEnabled}
                                                />
                                                <span className="time-separator">h</span>
                                                <TextInput
                                                    type={TextInputType.Integer}
                                                    value={state.laborMinutes?.toString() ?? ""}
                                                    onChange={(v) => viewModel.updateField("laborMinutes", v ? Number(v) : null)}
                                                    onFocus={(event) => event.target.select()}
                                                    min={0}
                                                    isEnabled={isEnabled}
                                                />
                                                <span className="time-separator">m</span>
                                            </div>
                                        )}
                                    </FormField>

                                    <FormField label="Actual Labor Cost">
                                        {state.isSaved ? (
                                            renderReadonly(costLabel(state.laborCost))
                                        ) : (
                                            <TextInput
                                                type={TextInputType.Money}
                                                value={state.laborCost ?? ""}
                                                onChange={(v) =>
                                                    viewModel.updateField(
                                                        "laborCost",
                                                        v
                                                    )
                                                }
                                                onFocus={(event) => event.target.select()}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                    </FormField>

                                    <FormField label="Actual Parts Cost">
                                        {state.isSaved ? (
                                            renderReadonly(costLabel(state.partsCost))
                                        ) : (
                                            <TextInput
                                                type={TextInputType.Money}
                                                value={state.partsCost ?? ""}
                                                onChange={(v) =>
                                                    viewModel.updateField(
                                                        "partsCost",
                                                        v 
                                                    )
                                                }
                                                onFocus={(event) => event.target.select()}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                    </FormField>

                                    <FormField label="Misc Cost">
                                        {state.isSaved ? (
                                            renderReadonly(costLabel(state.otherCost))
                                        ) : (
                                            <TextInput
                                                type={TextInputType.Money}
                                                value={state.otherCost ?? ""}
                                                onChange={(v) =>
                                                    viewModel.updateField(
                                                        "otherCost",
                                                        v 
                                                    )
                                                }
                                                onFocus={(event) => event.target.select()}
                                                isEnabled={isEnabled}
                                            />
                                        )}
                                    </FormField>
                                </Grid>

                                <Grid columns={1} className="mech-notes-row">
                                    <FormField label="Mechanics Notes">
                                        {state.isSaved ? (
                                            renderReadonly(state.notes)
                                        ) : (
                                            <TextArea
                                                value={state.notes}
                                                onChange={(v) => viewModel.updateField("notes", v)}
                                                maxLength={2000}
                                                disabled={!isEnabled}
                                                placeholder="Add any specific instructions, observations, or notes about this service"
                                            />
                                        )}
                                    </FormField>
                                </Grid>
                                {(state.workRequestType == WorkRequestType.Preventative || state.workRequestType == WorkRequestType.Inspection) &&
                                    <Grid columns={1}>
                                        {state.areTasksLoading ? (
                                            <LoadingIndicator isLoading={state.areTasksLoading} />
                                        ) : (
                                            <FormField
                                                label={
                                                    <>
                                                        <span>Tasks to be Performed</span>
                                                    </>
                                                }
                                                direction={FormFieldDirection.Column}
                                            >
                                                <Grid columns={1} gap={GridGap.Normal}>
                                                    <TaskList type={state.workRequestType} tasks={[...(state.tasks ?? [])].sort((a, b) => a.label.localeCompare(b.label))} onChange={(tasks) => {
                                                        viewModel.updateField("tasks", tasks)
                                                        //viewModel.updateField("initialtasks", tasks)
                                                    }} />
                                                </Grid>
                                            </FormField>
                                        )}
                                    </Grid>
                                }
                                <Grid columns={1}>
                                    {state.isSaved ? (
                                        renderReadonly(
                                            state.attachments.length > 0
                                                ? `${state.attachments.length} file(s) attached`
                                                : "No attachments"
                                        )
                                    ) : (
                                        <AttachmentList
                                            attachments={state.attachments}
                                            onAttachmentsChanged={(files, formData) => {
                                                viewModel.updateField("isLoading", true)
                                                viewModel.attachmentsChanged(files, formData)
                                                viewModel.updateField("isLoading", false)
                                            }
                                            }
                                            onAttachmentDeleteConfirm={(attachment) => {
                                                viewModel.updateField("isLoading", true)
                                                viewModel.attachmentDeleted(attachment)
                                                viewModel.updateField("isLoading", false)
                                            }
                                            }
                                            onImageRotate90={(attachment) => viewModel.rotateImage(attachment, 90)}
                                            onError={(errors) => viewModel.attachmentError(errors)}
                                            disabled={!isEnabled}
                                        />
                                    )}
                                </Grid>
                                {state.id && (
                                    <Grid columns={1}>
                                        <CollapsibleFormField
                                            label="Service Item History"
                                            isLoading={state.historyIsLoading}
                                            onClick={() => viewModel.loadServiceItemHistory()}
                                            isCollapsed={true}
                                        >
                                            <HistoryTable
                                                history={state.history ?? []}
                                                statusLabelCreator={(status: string) => status}
                                            />
                                        </CollapsibleFormField>
                                    </Grid>
                                )}
                            </GridCont>
                        )}
                    </div>
                </>
            )}
        </>
    )
}
